import { useEffect, useMemo, useState } from "react";
import { signupAdmin } from "@services/auth";
import { sanitizeFormValues } from "@utils/sanitize";

import StepIndicator from "@components/admin-signup/StepIndicator";
import AdminWarningCard from "@components/admin-signup/AdminWarningCard";
import SuccessCard from "@components/admin-signup/SuccessCard";
import ClinicSection from "@components/admin-signup/ClinicSection";
import OwnerSection from "@components/admin-signup/OwnerSection";
import FormActions from "@components/admin-signup/FormActions";
import { AlertBanner } from "@components/admin-signup/AlertBanner";

import { getAllTimezones } from "@components/admin-signup/timezones";
import {
  isValidEmail,
  isValidPassword,
} from "@components/admin-signup/validators";
import {
  fetchAgentPurposes,
  fetchAgentQuestions,
  saveClinicAgentQuestionsAll,
} from "@services/agentQuestions";

export default function AdminSignup() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [success, setSuccess] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const [purposes, setPurposes] = useState([]);
  const [questionLibrary, setQuestionLibrary] = useState([]);
  const [questionLibraryLoading, setQuestionLibraryLoading] = useState(true);

  const [agentQuestionsByPurpose, setAgentQuestionsByPurpose] = useState({});

  const defaultTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }, []);

  const ALL_TIMEZONES = useMemo(getAllTimezones, []);

  const [form, setForm] = useState({
    clinic_name: "",
    clinic_address: "",
    clinic_phone: "",
    clinic_info: "",
    clinic_agent_name: "",
    welcome_message: "",
    clinic_country: "US",
    clinic_city: "",
    preferred_timezone: defaultTimezone,
    elevenlabs_agent_id: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  const [showPassword, setShowPassword] = useState(false);

  const currentStep = useMemo(() => {
    if (success) return 3;
    if ((form.clinic_name || "").trim()) return 2;
    return 1;
  }, [form.clinic_name, success]);
  useEffect(() => {
    (async () => {
      setQuestionLibraryLoading(true);
      try {
        const [pRes, qRes] = await Promise.all([
          fetchAgentPurposes(),
          fetchAgentQuestions(),
        ]);

        // Backend returns [{ value, is_optional }, ...]; older shape was plain
        // strings. Normalize to string values (used as object keys and rendered
        // as text downstream). Mirrors useAdminClinics.js / useAdminAgentQuestions.js.
        const pList = (pRes?.data || []).map((p) =>
          typeof p === "string" ? p : p?.value
        ).filter(Boolean);
        setPurposes(pList);

        const qList = qRes?.data || [];
        setQuestionLibrary(qList);

        // initialize shape so editor works immediately
        const init = {};
        for (const p of pList) init[p] = [];
        setAgentQuestionsByPurpose(init);
      } finally {
        setQuestionLibraryLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(""), 3000);
    return () => clearTimeout(t);
  }, [successMessage]);

  const onChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const resetForm = () => {
    setForm({
      clinic_name: "",
      clinic_address: "",
      clinic_phone: "",
      clinic_info: "",
      clinic_agent_name: "",
      welcome_message: "",
      clinic_country: "US",
      clinic_city: "",
      preferred_timezone: defaultTimezone,
      elevenlabs_agent_id: "",
      email: "",
      password: "",
      confirm_password: "",
    });

    // reset agent questions editor
    const init = {};
    for (const p of purposes) init[p] = [];
    setAgentQuestionsByPurpose(init);
  };

  const clearAll = () => {
    setError("");
    setSuccess(null);
    setSuccessMessage("");
    resetForm();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(null);
    setSuccessMessage("");

    if (!isValidEmail(form.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!isValidPassword(form.password)) {
      setError(
        "Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character.",
      );
      return;
    }

    if (form.password !== form.confirm_password) {
      setError("Passwords do not match.");
      return;
    }

    if (!(form.elevenlabs_agent_id || "").trim()) {
      setError("ElevenLabs Agent ID is required.");
      return;
    }

    const hasAnyAgentQuestions =
      agentQuestionsByPurpose &&
      Object.values(agentQuestionsByPurpose).some(
        (arr) => Array.isArray(arr) && arr.length,
      );

    setLoading(true);
    try {
      const accessToken = localStorage.getItem("access_token") || "";

      const payload = sanitizeFormValues({
        ...form,
        preferred_timezone: (form.preferred_timezone || "UTC").trim() || "UTC",
      });

      const result = await signupAdmin({ payload, accessToken });
      const data = result?.data || {};
      const clinicId = data.clinic_id;
      console.log(clinicId);
      if (clinicId && hasAnyAgentQuestions) {
        await saveClinicAgentQuestionsAll(clinicId, agentQuestionsByPurpose);
      }
      setSuccess({
        message: result?.message || "Owner account created.",
        email: form.email,
        clinic_id: data.clinic_id,
        clinic_name: data.clinic_name,
        preferred_timezone: data.preferred_timezone,
        assigned_phone_number: data.assigned_phone_number,
        elevenlabs_agent_id: data.elevenlabs_agent_id,
        provisioning_warnings: data.provisioning_warnings,
      });

      setSuccessMessage("✅ Success! Clinic and owner account created.");
      resetForm();
    } catch (err) {
      setError(err.message || "Failed to create owner account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-lg p-6 md:p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-slate-900">
              Create Owner Account
            </h1>
            <div className="mt-2 h-1 w-16 rounded-full bg-indigo-600" />
            <p className="mt-3 text-sm text-slate-600">
              Creates a clinic record first, then creates an owner user linked
              to that clinic.
            </p>
          </div>

          {successMessage && (
            <div className="mb-4">
              <AlertBanner
                variant="success"
                onDismiss={() => setSuccessMessage("")}
              >
                {successMessage}
              </AlertBanner>
            </div>
          )}

          <StepIndicator step={currentStep} />

          <AdminWarningCard />

          {error && (
            <div className="mb-4">
              <AlertBanner variant="error" onDismiss={() => setError("")}>
                {error}
              </AlertBanner>
            </div>
          )}

          <SuccessCard success={success} />

          <form onSubmit={onSubmit} className="grid gap-6">
            <ClinicSection
              form={form}
              onChange={onChange}
              ALL_TIMEZONES={ALL_TIMEZONES}
              purposes={purposes}
              questionLibrary={questionLibrary}
              questionLibraryLoading={questionLibraryLoading}
              agentQuestionsByPurpose={agentQuestionsByPurpose}
              setAgentQuestionsByPurpose={setAgentQuestionsByPurpose}
            />

            <OwnerSection
              form={form}
              onChange={onChange}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
            />

            <FormActions loading={loading} onClear={clearAll} />
          </form>
        </div>
      </div>
    </div>
  );
}
